import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type {
  ProjectKind,
  ProjectTransferDestinationDto,
  ProjectTransferRefusal,
  TransferProjectRequest,
} from '@aiai/shared';
import { fetchTransferDestinations, transferProject } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { transferErrorMessage, transferRefusalText } from '../lib/transferMessages';
import { workspacePath } from '../lib/workspacePath';
import { Callout } from './Callout';
import { Modal } from './Modal';
import { Spinner } from './Spinner';
import { SegmentedTabs } from './Tabs';

type Mode = 'mine' | 'other';

/** Where the project goes: one of my workspaces, or anyone's by slug / owner email. */
type Target =
  | { mode: 'mine'; destination: ProjectTransferDestinationDto }
  | { mode: 'other'; value: string };

function requestBody(t: Target): TransferProjectRequest {
  if (t.mode === 'mine') return { toOrgId: t.destination.orgId };
  const v = t.value.trim();
  return v.includes('@') ? { toEmail: v } : { toOrgSlug: v.toLowerCase() };
}

/** Every refusal on a failed transfer's 4xx body (the preflight lists all of them). */
function bodyRefusals(err: unknown): ProjectTransferRefusal[] {
  if (!axios.isAxiosError(err)) return [];
  const data = err.response?.data as { refusals?: ProjectTransferRefusal[] } | undefined;
  return Array.isArray(data?.refusals) ? data.refusals : [];
}

function RefusalList({ refusals }: { refusals: ProjectTransferRefusal[] }) {
  return (
    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-danger">
      {refusals.map((r, i) => (
        <li key={`${r.code}-${i}`}>{transferRefusalText(r)}</li>
      ))}
    </ul>
  );
}

/**
 * Move a project or prototype to another workspace. Step 1 picks the
 * destination (my other owned workspaces with their preflight, or another
 * person's workspace by slug / owner email); step 2 confirms what moves and
 * what resets. Same-owner moves happen immediately and land the caller in the
 * destination; another owner's workspace gets a pending request.
 */
export function TransferProjectDialog({
  open,
  onClose,
  project,
}: {
  open: boolean;
  onClose: () => void;
  project: { id: string; name: string; kind: ProjectKind; ref: string; factory?: boolean };
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>('mine');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [other, setOther] = useState('');
  const [target, setTarget] = useState<Target | null>(null);
  const [pendingUntil, setPendingUntil] = useState<string | null>(null);

  const noun = project.kind === 'prototype' ? 'prototype' : 'project';

  const destinationsQuery = useQuery({
    queryKey: qk.transferDestinations(project.id),
    queryFn: () => fetchTransferDestinations(project.id),
    enabled: open,
  });
  const destinations = destinationsQuery.data ?? [];
  const selected = destinations.find((d) => d.orgId === selectedOrgId) ?? null;

  const mutation = useMutation({
    mutationFn: (t: Target) => transferProject(project.id, requestBody(t)),
    onSuccess: (result, t) => {
      queryClient.invalidateQueries({ queryKey: qk.transfers });
      if (result.status === 'pending') {
        setPendingUntil(result.expiresAt);
        return;
      }
      for (const key of [qk.projects, qk.prototypes, qk.myProjects, qk.myPrototypes]) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      // Moved now: only a same-owner move, so the destination is one of mine.
      const slug = t.mode === 'mine' ? t.destination.slug : null;
      const section = project.kind === 'prototype' ? '/prototypes' : '/projects';
      navigate(slug ? workspacePath(slug, `${section}/${project.ref}`) : section);
    },
  });

  const reset = () => {
    setMode('mine');
    setSelectedOrgId(null);
    setOther('');
    setTarget(null);
    setPendingUntil(null);
    mutation.reset();
  };
  const close = () => {
    if (mutation.isPending) return;
    reset();
    onClose();
  };

  const canContinue =
    mode === 'mine' ? !!selected && selected.preflight.ok : other.trim().length > 0;
  const next = () => {
    if (mode === 'mine' && selected) setTarget({ mode: 'mine', destination: selected });
    else if (mode === 'other') setTarget({ mode: 'other', value: other });
  };

  const footer = pendingUntil ? (
    <button type="button" className="btn-primary" onClick={close}>
      Done
    </button>
  ) : target ? (
    <>
      <button
        type="button"
        className="btn-secondary"
        onClick={() => {
          setTarget(null);
          mutation.reset();
        }}
        disabled={mutation.isPending}
      >
        Back
      </button>
      <button
        type="button"
        className="btn-danger"
        onClick={() => mutation.mutate(target)}
        disabled={mutation.isPending}
      >
        {mutation.isPending && <Spinner />}
        {target.mode === 'mine' ? `Transfer ${noun}` : 'Send transfer request'}
      </button>
    </>
  ) : (
    <>
      <button type="button" className="btn-secondary" onClick={close}>
        Cancel
      </button>
      <button type="button" className="btn-primary" onClick={next} disabled={!canContinue}>
        Continue
      </button>
    </>
  );

  return (
    <Modal open={open} onClose={close} title={`Transfer ${noun}`} widthClass="max-w-lg" footer={footer}>
      {pendingUntil ? (
        <Callout tone="success" kicker="Transfer requested">
          The destination workspace&apos;s owner can accept or decline it until{' '}
          {formatDateTime(pendingUntil)}. Nothing moves until they accept; you can cancel it from
          this {noun}&apos;s danger zone.
        </Callout>
      ) : target ? (
        <ConfirmStep project={project} noun={noun} target={target} error={mutation.error} />
      ) : (
        <div className="space-y-4">
          <SegmentedTabs
            ariaLabel="Destination"
            value={mode}
            onChange={setMode}
            options={[
              { value: 'mine', label: 'My workspaces' },
              { value: 'other', label: "Another person's workspace" },
            ]}
          />
          {mode === 'mine' ? (
            destinationsQuery.isLoading ? (
              <p className="flex items-center gap-2 text-sm text-ink-muted">
                <Spinner /> Checking your workspaces…
              </p>
            ) : destinationsQuery.isError ? (
              <Callout tone="danger">
                {errorMessage(destinationsQuery.error, 'Could not load your workspaces')}
              </Callout>
            ) : destinations.length === 0 ? (
              <p className="text-sm text-ink-muted">
                You don&apos;t own another workspace. To hand this {noun} to someone else, use
                &ldquo;Another person&apos;s workspace&rdquo;.
              </p>
            ) : (
              <ul className="space-y-2" aria-label="Your workspaces">
                {destinations.map((d) => (
                  <li key={d.orgId}>
                    <label
                      className={`flex gap-3 rounded-lg border px-3 py-2 ${
                        d.preflight.ok ? 'cursor-pointer border-line hover:bg-surface-2' : 'border-line opacity-80'
                      } ${selectedOrgId === d.orgId ? 'border-signal' : ''}`}
                    >
                      <input
                        type="radio"
                        name="transfer-destination"
                        className="mt-1"
                        value={d.orgId}
                        checked={selectedOrgId === d.orgId}
                        disabled={!d.preflight.ok}
                        onChange={() => setSelectedOrgId(d.orgId)}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-ink">{d.name}</span>
                        <span className="block font-mono text-2xs text-ink-faint">
                          {d.slug} · {d.plan}
                        </span>
                        {!d.preflight.ok && <RefusalList refusals={d.preflight.refusals} />}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <label className="block space-y-1">
              <span className="text-xs font-medium text-ink">Workspace slug or owner&apos;s email</span>
              <input
                className="input w-full"
                value={other}
                onChange={(e) => setOther(e.target.value)}
                placeholder="their-workspace or owner@example.com"
                autoComplete="off"
              />
              <span className="block text-xs text-ink-faint">
                The workspace&apos;s owner gets a request to accept or decline (it expires after 7
                days). An email works when that person owns exactly one workspace.
              </span>
            </label>
          )}
        </div>
      )}
    </Modal>
  );
}

function ConfirmStep({
  project,
  noun,
  target,
  error,
}: {
  project: { name: string; kind: ProjectKind; factory?: boolean };
  noun: string;
  target: Target;
  error: unknown;
}) {
  const isPrototype = project.kind === 'prototype';
  const refusals = error ? bodyRefusals(error) : [];
  const destination =
    target.mode === 'mine' ? target.destination.name : target.value.trim();

  return (
    <div className="space-y-3 text-sm text-ink-muted">
      <p>
        {target.mode === 'mine' ? 'Move' : 'Ask to move'}{' '}
        <span className="font-medium text-ink">{project.name}</span> to{' '}
        <span className="font-medium text-ink">{destination}</span>
        {target.mode === 'mine'
          ? '. It moves right away.'
          : '. It moves only once that workspace’s owner accepts.'}
      </p>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Moves with it</p>
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          <li>All issues and their history, run history and pull-request links</li>
          <li>Loops and threads</li>
          <li>The project wiki and memory</li>
          <li>The {noun}'s own Integration API tokens (connected apps keep working)</li>
          {isPrototype && <li>The live prototype, its secrets and custom domains</li>}
        </ul>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Resets</p>
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          <li>Project access grants — members of this workspace lose access</li>
          <li>Pending invites for this {noun}</li>
          {isPrototype && (
            <li>
              The workspace prototype domain follows the destination workspace. The default URL is
              unchanged.
            </li>
          )}
        </ul>
      </div>

      {isPrototype && project.factory && (
        <Callout tone="warning" kicker="Factory prototype">
          This prototype was created through the Integration API. Transferring it detaches it from
          that integration: the Integration API link and its webhooks stop applying to it.
        </Callout>
      )}

      {!!error && (
        <Callout tone="danger">
          {transferErrorMessage(error, `Could not transfer the ${noun}`)}
          {refusals.length > 1 && <RefusalList refusals={refusals} />}
        </Callout>
      )}
    </div>
  );
}
